import PropTypes, { object } from 'prop-types'
import React, { useLayoutEffect, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper-v9/react'
import { Navigation, Autoplay, A11y } from 'swiper-v9'
import Cookies from 'universal-cookie'
import { Collapse, styled } from '@mui/material'
import TopBarInfoSlide from './TopBarInfoSlide'
import TopBarInfoControls from './TopBarInfoControls'
import 'swiper-v9/css'
import 'swiper-v9/css/navigation'

const cookies = new Cookies()

const SliderContainer = styled('section')(({ theme, backgroundColor, isIconOnTop, Alignment, hasCountdown }) => ({
  height: '48px',
  width: '100vw',
  maxWidth: '100%',
  overflow: 'hidden',
  display: 'flex',
  justifyContent: Alignment === 'Left' ? 'flex-start' : 'center',
  alignItems: 'center',
  backgroundColor,
  position: 'relative',
  paddingLeft: Alignment === 'Left' ? '20px' : '0',
  [theme.breakpoints.down('md')]: {
    height: isIconOnTop ? '53px' : '48px',
    ...(hasCountdown && {
      height: '62px !important',
    }),
  },
}))

const StyledSwiperSlide = styled(SwiperSlide)(({ theme }) => ({
  height: '100%',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    width: '100vw',
  },
}))

const TopBarInfoSlider = ({ data, isDrawerOpen, isMobile = false }) => {
  const infoClosedCookie = cookies.get('__Host-rtg_top_info_banner')
  const [swiperInstance, setSwiperInstance] = useState(null)
  const [isClosed, setIsClosed] = useState(infoClosedCookie ?? false)
  const slides = data?.strapiHeader?.TopBarInfoSlider?.TopBarInfoSlider?.TopBarInfo
  const [currentSlide, setCurrentSlide] = useState({
    backgroundColor: slides?.[0]?.TopBarInfo?.BackgroundColor?.ColorHex || '#07263B',
    color: slides?.[0]?.TopBarInfo?.ArrowColor?.ColorHex || slides?.[0]?.TopBarInfo?.TextColor?.ColorHex || '#ffffff',
    variant: slides?.[0]?.TopBarInfo?.Variant || 'IconAdjacent',
    Alignment: slides?.[0]?.TopBarInfo?.Alignment || 'Center',
  })

  const [utmSlide, setUtmSlide] = useState(null)
  const utmSlideData = [data?.strapiHeader?.TopBarInfoSlider?.TopBarInfoSlider?.UtmSlide]
  const utmCampaignList = data?.strapiHeader?.TopBarInfoSlider?.TopBarInfoSlider?.UtmCampaigns?.toLowerCase().split(',')
  const isIconOnTop = currentSlide.variant === 'IconOnTop' && isMobile
  const countdownNotExpired = slides?.some(
    slide => new Date(slide.TopBarInfo?.Countdown?.Countdown?.ExpirationDate) > new Date(),
  )
  const hasCountdown =
    slides?.map(slide => slide.TopBarInfo?.Countdown?.Countdown).some(countdown => countdown) && countdownNotExpired

  const updateCurrentSlide = (slide, index = 0) => {
    const { BackgroundColor, TextColor, ArrowColor, Variant, Header, Alignment } = slide.TopBarInfo
    const backgroundColor = BackgroundColor?.ColorHex || '#07263B'
    const color = ArrowColor?.ColorHex || TextColor?.ColorHex || '#ffffff'
    const variant = Variant || 'IconAdjacent'
    const header =
      Header ||
      `${data?.strapiHeader?.TopBarInfoSlider?.TopBarInfoSlider?.Title} Slider: Slide ${index} of ${slides.length}`
    setCurrentSlide({ backgroundColor, color, variant, header, index, Alignment })
  }

  const handleSlideChange = swiper => {
    const activeSlide = slides[swiper.realIndex]
    if (activeSlide) updateCurrentSlide(activeSlide, swiper.realIndex)
  }

  useLayoutEffect(() => {
    // Check for UTM campaign
    const urlParams = new URLSearchParams(window.location.search)
    const utmCampaign = urlParams
      .get('utm_campaign')
      ?.toLowerCase()
      ?.replace(/\s/g, '')
    if (utmCampaign && utmCampaignList?.includes(utmCampaign)) {
      sessionStorage.setItem('utmSlide', JSON.stringify(utmSlideData[0]))
      setUtmSlide(utmSlideData)
    } else {
      const storedUtmSlide = sessionStorage.getItem('utmSlide')
      if (storedUtmSlide) {
        setUtmSlide([JSON.parse(storedUtmSlide)])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useLayoutEffect(() => {
    if (utmSlide?.[0]) {
      updateCurrentSlide(utmSlide[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utmSlide])

  if (!slides || isClosed) return null

  return (
    <Collapse in={isMobile ? !isDrawerOpen : true} easing={{ enter: 'ease-in' }} timeout={200} unmountOnExit>
      <SliderContainer
        backgroundColor={currentSlide.backgroundColor}
        isIconOnTop={isIconOnTop}
        Alignment={currentSlide.Alignment}
        aria-label="Top Bar Slider"
        hasCountdown={hasCountdown}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            maxWidth: '1440px',
          }}
        >
          <Swiper
            modules={[Autoplay, Navigation, A11y]}
            slidesPerView={1}
            spaceBetween={0}
            breakpoints={{
              0: {
                spaceBetween: 100,
              },
              1024: {
                spaceBetween: 0,
              },
            }}
            onSlideChange={handleSlideChange}
            a11y={{
              enabled: true,
              slideLabelMessage: currentSlide.header,
            }}
            navigation={{
              nextEl: '.tbs-swiper-next',
              prevEl: '.tbs-swiper-prev',
            }}
            onBeforeInit={swiper => {
              swiper.params.navigation.prevEl = '.tbs-swiper-prev'
              swiper.params.navigation.nextEl = '.tbs-swiper-next'
            }}
            onSwiper={swiper => setSwiperInstance(swiper)}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            loop
            style={{ height: '100%', width: '100%' }}
          >
            {(utmSlide || slides).map((slide, index) => {
              const isActiveSlide = index === currentSlide.index
              return (
                <StyledSwiperSlide key={slide.id} aria-hidden={!isActiveSlide}>
                  <TopBarInfoSlide
                    data={slide.TopBarInfo}
                    isMobile={isMobile}
                    isActive={isActiveSlide}
                    hasActiveCountdown={hasCountdown}
                  />
                </StyledSwiperSlide>
              )
            })}
          </Swiper>
          <TopBarInfoControls
            swiper={swiperInstance}
            setIsClosed={setIsClosed}
            bgColor={currentSlide.backgroundColor}
          />
        </div>
      </SliderContainer>
    </Collapse>
  )
}

TopBarInfoSlider.propTypes = {
  data: PropTypes.shape({
    strapiHeader: PropTypes.shape({
      TopBarInfoSlider: PropTypes.shape({
        TopBarInfoSlider: PropTypes.shape({
          TopBarInfo: PropTypes.arrayOf(object),

          UtmSlide: PropTypes.object,
          UtmCampaigns: PropTypes.string,

          Title: PropTypes.string.isRequired,
        }),
      }),
    }),
  }),

  isDrawerOpen: PropTypes.bool,
  isMobile: PropTypes.bool,
}

export default TopBarInfoSlider
